import { memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';

import type { PendingCutsReport } from '../../../../redux/api/pending-cuts';
import type { LocationLabelFormatter } from '../../../../utils/locationLabel';
import { METHOD_LABELS, PendingCutsFilters } from './filters';
import {
	barLocationText,
	dueText,
	groupCounts,
	inches,
	jobText,
	piecesDetail,
	statusText,
	toRows,
} from './rows';

interface Option {
	value: string;
	label: string;
}

interface PrintablePendingCutsReportProps {
	data: PendingCutsReport;
	filters: PendingCutsFilters;
	buildingOptions: Option[];
	formatLocation: LocationLabelFormatter;
}

/** Inches on landscape Letter (10.2in printable); Job takes what is left (about 1.95in). */
const COLUMNS: { label: string; width?: string; className?: string }[] = [
	{
		label: 'Verified',
		width: '0.55in',
		className: 'pending-cuts-print__check',
	},
	{ label: 'Bar', width: '0.75in' },
	{ label: 'Location', width: '1.35in' },
	{ label: 'Bar length', width: '1.05in' },
	{ label: 'Flags', width: '1.2in' },
	{ label: 'Cut length', width: '0.8in' },
	{ label: 'Pcs to cut', width: '1.1in' },
	{ label: 'Job' },
	{ label: 'Due', width: '0.6in' },
	{ label: 'Status', width: '0.85in' },
];

const describeFilters = (
	filters: PendingCutsFilters,
	buildingOptions: Option[]
) => {
	const parts = [`Method: ${METHOD_LABELS[filters.method]}`];
	if (filters.buildings.length) {
		const names = filters.buildings.map(
			(id) => buildingOptions.find((o) => o.value === id)?.label ?? id
		);
		parts.push(`Buildings: ${names.join(', ')}`);
	}
	if (filters.bar) parts.push(`Bar: ${filters.bar}`);
	return parts.join(' · ');
};

const PrintablePendingCutsReport = ({
	data,
	filters,
	buildingOptions,
	formatLocation,
}: PrintablePendingCutsReportProps) => {
	const printedAt = dayjs().format('MMM D, YYYY h:mm A');
	const showBuilding = filters.buildings.length !== 1;
	const sections = useMemo(
		() =>
			data.groups.map((group) => ({
				group,
				bars: group.bars.map((bar) => ({
					id: bar.id,
					rows: toRows([{ ...group, bars: [bar] }]),
				})),
			})),
		[data.groups]
	);
	const { summary } = data;

	return createPortal(
		<div className="pending-cuts-print">
			<table className="pending-cuts-print__page">
				<thead>
					<tr>
						<td className="pending-cuts-print__header">
							<div className="pending-cuts-print__title">
								Pending Cuts Audit — {METHOD_LABELS[filters.method]}
							</div>
							<div className="pending-cuts-print__meta">
								<span>{describeFilters(filters, buildingOptions)}</span>
								<span>Printed {printedAt}</span>
							</div>
						</td>
					</tr>
				</thead>
				<tfoot>
					<tr>
						<td className="pending-cuts-print__footer">
							PartsBadger · Pending Cuts · Tick “Verified” when the bar is
							physically at the listed location, its length matches, and the
							cuts look right for the job.
						</td>
					</tr>
				</tfoot>
				<tbody>
					<tr>
						<td className="pending-cuts-print__body">
							<div className="pending-cuts-print__summary">
								<strong>
									{summary.reservations} reservations · {summary.bars} bars ·{' '}
									{summary.pending_pieces} pieces to cut ·{' '}
									{summary.flagged_bars} bars flagged
								</strong>
							</div>

							{data.truncated && (
								<p className="pending-cuts-print__warning">
									List cut off at the first {data.row_cap.toLocaleString()}{' '}
									reservations. Add a building or bar filter to print the rest.
								</p>
							)}

							{sections.length === 0 && (
								<p>Nothing is waiting to be cut for this filter.</p>
							)}

							{sections.length > 0 && (
								<table className="pending-cuts-print__data">
									<colgroup>
										{COLUMNS.map((col) => (
											<col
												key={col.label}
												style={col.width ? { width: col.width } : undefined}
											/>
										))}
									</colgroup>
									<thead>
										<tr>
											{COLUMNS.map((col) => (
												<th key={col.label} className={col.className}>
													{col.label}
												</th>
											))}
										</tr>
									</thead>
									{sections.map(({ group, bars }) => [
										<tbody
											key={group.material_id}
											className="pending-cuts-print__heading"
										>
											<tr>
												<th
													colSpan={COLUMNS.length}
													className="pending-cuts-print__material"
												>
													{group.material_name}
													<span className="pending-cuts-print__count">
														{' '}
														· {groupCounts(group)}
													</span>
												</th>
											</tr>
										</tbody>,
										...bars.map(({ id, rows }) => (
											<tbody key={id} className="pending-cuts-print__bar">
												{rows.map((row) => {
													if (row.kind !== 'reservation') return null;
													const { bar, reservation: r, barSpan } = row;
													const span = barSpan || undefined;
													const flags = [
														...(barSpan ? bar.flags : []),
														...r.flags,
													];
													const locationLabel = barLocationText(
														bar,
														formatLocation,
														showBuilding
													);
													return (
														<tr key={row.key}>
															{barSpan > 0 && (
																<>
																	<td
																		rowSpan={span}
																		className="pending-cuts-print__check"
																	>
																		<span className="pending-cuts-print__box" />
																	</td>
																	<td
																		rowSpan={span}
																		className="pending-cuts-print__bar-id"
																	>
																		{bar.friendly_id ?? '—'}
																	</td>
																	<td
																		rowSpan={span}
																		className="pending-cuts-print__wrap"
																	>
																		{locationLabel ? (
																			locationLabel
																		) : (
																			<strong>NO LOCATION</strong>
																		)}
																	</td>
																	<td rowSpan={span}>
																		{inches(bar.current_length)}
																		<div className="pending-cuts-print__sub">
																			{inches(bar.remaining_after_cuts)} after
																			cuts
																		</div>
																	</td>
																</>
															)}
															<td className="pending-cuts-print__wrap">
																{flags.length ? (
																	<strong>{flags.join('; ')}</strong>
																) : (
																	''
																)}
															</td>
															<td>
																{inches(r.cut_length)}
																<div className="pending-cuts-print__sub">
																	kerf {inches(r.saw_kerf)}
																</div>
															</td>
															<td>
																<span className="pending-cuts-print__pcs">
																	{r.pending_quantity}
																</span>
																<div className="pending-cuts-print__sub">
																	{piecesDetail(r)}
																</div>
															</td>
															<td className="pending-cuts-print__wrap">
																{jobText(r)}
															</td>
															<td>{dueText(r)}</td>
															<td>{statusText(r)}</td>
														</tr>
													);
												})}
											</tbody>
										)),
									])}
								</table>
							)}

							<div className="pending-cuts-print__signoff">
								<span>Audited by: ______________________________</span>
								<span>Date: ______________</span>
								<span>Signature: ______________________________</span>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>,
		document.body
	);
};

const MemoizedPrintablePendingCutsReport = memo(PrintablePendingCutsReport);

export default MemoizedPrintablePendingCutsReport;
